import {DocumentContext, DocumentEntityType} from '@sollapay/enums';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {documentKeys} from '@/features/documents/hooks';
import {trustAccountKeys} from '@/features/trust-accounts/hooks';
import {useApiClient} from '@/hooks';

import {activationApi} from '../api';

import type {PackageType} from '@sollapay/enums';
import type {
  SignMainFatcaCrsPayload,
  SignSupplementaryFatcaCrsPayload,
  UpsertSellersPayload,
  TrustScopeUpsertPayload,
  UpsertTrustAssetPayload
} from '@sollapay/types';

export const trustAccountActivationKeys = {
  trustDetailsPackage: (id: string) =>
    [...trustAccountKeys.all, 'trust-details-package', id] as const,
  compliancePackage: (id: string) => [...trustAccountKeys.all, 'compliance-package', id] as const
};

/**
 * Hook to fetch trust details package (scope + developer + documents)
 */
export function useTrustDetailsPackageQuery(id: string, options?: {enabled?: boolean}) {
  const client = useApiClient();

  return useQuery({
    queryKey: trustAccountActivationKeys.trustDetailsPackage(id),
    queryFn: () => activationApi.getTrustDetailsPackage(client, id),
    enabled: !!id && (options?.enabled ?? true)
  });
}

/**
 * Hook to fetch compliance package (fatcaCrs + legal documents)
 */
export function useCompliancePackageQuery(id: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: trustAccountActivationKeys.compliancePackage(id),
    queryFn: () => activationApi.getCompliancePackage(client, id),
    select: response => response.data,
    enabled: !!id
  });
}

/**
 * Hook to upsert scope details for a trust account
 */
export function useUpsertScopeMutation(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TrustScopeUpsertPayload) =>
      activationApi.upsertScope(client, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: trustAccountActivationKeys.trustDetailsPackage(id)});
      queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(id)});
    }
  });
}

/**
 * Hook to upsert the trust account's one shared asset (property/land + reference price)
 */
export function useUpsertAssetMutation(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertTrustAssetPayload) =>
      activationApi.upsertAsset(client, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: trustAccountActivationKeys.trustDetailsPackage(id)});
      queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(id)});
    }
  });
}

/**
 * Hook to upsert the selling-party roster for a trust account
 */
export function useUpsertSellersMutation(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpsertSellersPayload) => activationApi.upsertSellers(client, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: trustAccountActivationKeys.trustDetailsPackage(id)});
      queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(id)});
      queryClient.invalidateQueries({
        queryKey: documentKeys.list(
          DocumentEntityType.TRUST_ACCOUNT,
          id,
          DocumentContext.TRUST_DETAILS
        )
      });
    }
  });
}

/**
 * Hook to sign the main FATCA/CRS declaration for a trust account
 */
export function useSignMainFatcaCrsMutation(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SignMainFatcaCrsPayload) =>
      activationApi.signMainFatcaCrs(client, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: trustAccountActivationKeys.compliancePackage(id)});
      queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(id)});
    }
  });
}

/**
 * Hook to sign a supplementary FATCA/CRS declaration for a trust account
 */
export function useSignSupplementaryFatcaCrsMutation(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SignSupplementaryFatcaCrsPayload) =>
      activationApi.signSupplementaryFatcaCrs(client, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: trustAccountActivationKeys.compliancePackage(id)});
      queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(id)});
    }
  });
}

/**
 * Hook to submit a trust account for review
 * Invalidates the specific detail query and list queries on success
 */
export function useSubmitTrustAccountMutation(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => activationApi.submitTrustAccount(client, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trustAccountKeys.detail(id)
      });
      queryClient.invalidateQueries({
        queryKey: trustAccountKeys.lists()
      });
    }
  });
}

/**
 * Hook to send one package back to review after a clarification request
 * Invalidates the trust account detail and list queries so statuses refresh
 */
export function useResubmitPackageMutation(id: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (packageType: PackageType) =>
      activationApi.resubmitPackage(client, id, packageType),
    // The detail refresh is awaited so the confirmation never shows over the stale clarified
    // status; the lists catch up on their own.
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: trustAccountKeys.lists()});
      return queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(id)});
    }
  });
}
